import React, { FC, memo, useRef, useState } from "react";

import { Modal } from "@UI/Components/Modal/Modal";
import { Box } from "@UI/Components/Box/Box";
import { Heading } from "@UI/Components/Heading/Heading";
import { Button } from "@UI/Components/Button/Button";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import { PhotoUploader } from "@modules/ServiceRequest/ServiceRequestCreation/Components/Questions/PictureUploadQuestion/PhotoUploader";
import { TASK_PHOTO_CATEGORY } from "@modules/ServiceRequest/ServiceRequestCreation/config/constants";
import { usePhotoToast } from "@modules/ServiceRequest/ServiceRequestCreation/hooks/usePhotoToast";
import { IPhotoAttachment } from "@modules/ServiceRequest/ServiceRequestCreation/types";
import { updateServiceRequest } from "@modules/ServiceRequest/services/serviceRequests";
import {
  deletePhotoAttachmentService,
  updatePhotoAttachmentService,
} from "@Shared/services/photoAttachments";
import messages from "../../messages";
import creationMessages from "@modules/ServiceRequest/ServiceRequestCreation/messages";

// Mirrors the backend's own limits for the "task_photos" category
// (`PhotoAttachment::MAX_TASK_PHOTOS` / `ALLOWED_EXTENSIONS`, both in
// app/models/photo_attachment.rb) and the `caption_max_length` every trade's
// task-creation config agrees on (db/task_creation_config/*.json) — kept here
// rather than fetched, since this modal runs outside the creation wizard that
// normally supplies them per category.
const TASK_PHOTO_MAX_FILES = 10;
const TASK_PHOTO_ALLOWED_EXTENSIONS = ["png", "jpg", "webp", "jpeg", "heic"];
const TASK_PHOTO_CAPTION_MAX_LENGTH = 50;

// `overflowY` makes this a scroll container on *both* axes, so `PhotoCard`'s
// remove button is clipped where it overhangs the corner without room to do it.
const PHOTO_CARD_OVERHANG_GUTTER = "12px";

export interface IAddPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceRequestId: number;
  existingPhotos: IPhotoAttachment[];
  /** The task's photos as Confirm left them — captions included, and a fresh
   * upload's local preview as its thumbnail (see `PhotoUploader`). */
  onConfirmed: (photos: IPhotoAttachment[]) => void;
}

/**
 * Lets a task's owner add, remove and caption photos on an already-created
 * service request, reusing the task-creation wizard's own `PhotoUploader`.
 *
 * Nothing reaches the task until Confirm. A new file uploads straight away —
 * that is where its preview comes from — but unattached, the same way the
 * wizard's do; removals and captions are only held here. Confirm then writes
 * the captions and hands the task its whole photo id list (the edit endpoint
 * links the new ids and destroys the dropped ones). Dismissing discards it
 * all, deleting whatever this session uploaded.
 */
export const AddPhotosModal: FC<IAddPhotosModalProps> = memo(
  function AddPhotosModal({
    isOpen,
    onClose,
    serviceRequestId,
    existingPhotos,
    onConfirmed,
  }) {
    const { formatMessage } = useI18n();
    const showPhotoToast = usePhotoToast();
    const [isBusy, setIsBusy] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [photos, setPhotos] = useState(existingPhotos);
    // Every id uploaded since opening, kept or not: each one is an unattached
    // attachment that only Confirm gives an owner.
    const sessionUploadIds = useRef<number[]>([]);

    // Re-seeded on every opening, during render rather than in an effect, so
    // the uploader never mounts with the previous session's staged photos.
    const [wasOpen, setWasOpen] = useState(isOpen);
    if (isOpen !== wasOpen) {
      setWasOpen(isOpen);
      if (isOpen) {
        setPhotos(existingPhotos);
        sessionUploadIds.current = [];
      }
    }

    // Best effort: an orphan left behind is invisible to the user.
    const deleteSessionUploads = (keptIds: number[]) =>
      sessionUploadIds.current
        .filter((id) => !keptIds.includes(id))
        .forEach((id) => deletePhotoAttachmentService(id).catch(() => undefined));

    const handleDismiss = () => {
      // Mid-save, these uploads are the very ones being linked to the task.
      if (isSaving) return;
      deleteSessionUploads([]);
      onClose();
    };

    const handleConfirm = async () => {
      const ids = photos.map((photo) => photo.id);
      const changedCaptions = photos.filter(
        // `?? ""` on both sides: the server sends `null` for no caption.
        (photo) =>
          (photo.description ?? "") !==
          (existingPhotos.find(({ id }) => id === photo.id)?.description ?? "")
      );

      setIsSaving(true);
      try {
        await Promise.all([
          updateServiceRequest(serviceRequestId, { photo_attachments: ids }),
          ...changedCaptions.map((photo) =>
            updatePhotoAttachmentService(photo.id, {
              metadata: { description: photo.description },
            })
          ),
        ]);
        deleteSessionUploads(ids);
        onConfirmed(photos);
        onClose();
      } catch {
        showPhotoToast(
          formatMessage(creationMessages["taskCreation.photos.uploadError"])
        );
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleDismiss}
        size="lg"
        header={
          <Heading size="md">
            <FormattedMessage
              {...messages["newTaskShow.addPhotosModal.title"]}
            />
          </Heading>
        }
      >
        {/* Capped and independently scrollable, rather than the whole modal,
            so the header stays put and Confirm stays reachable however many
            of the up-to-10 photos are attached. */}
        <Box
          maxH={{ base: "45vh", md: "55vh" }}
          overflowY="auto"
          padding={PHOTO_CARD_OVERHANG_GUTTER}
          marginX={`-${PHOTO_CARD_OVERHANG_GUTTER}`}
        >
          <PhotoUploader
            value={photos.map((photo) => photo.id)}
            // The staged list is driven by the three callbacks below — there's
            // no separate form value to write back here.
            onChange={() => undefined}
            existingPhotos={photos}
            onPhotoUploaded={(photo) => {
              sessionUploadIds.current.push(photo.id);
              setPhotos((current) => [...current, photo]);
            }}
            onPhotoRemoved={(id) =>
              setPhotos((current) => current.filter((photo) => photo.id !== id))
            }
            onDescriptionSaved={(id, description) =>
              setPhotos((current) =>
                current.map((photo) =>
                  photo.id === id ? { ...photo, description } : photo
                )
              )
            }
            // No `photoAttachableId`: a new photo stays unattached until
            // Confirm links it to the task.
            category={TASK_PHOTO_CATEGORY}
            maxFiles={TASK_PHOTO_MAX_FILES}
            allowedExtensions={TASK_PHOTO_ALLOWED_EXTENSIONS}
            captionMaxLength={TASK_PHOTO_CAPTION_MAX_LENGTH}
            onBusyChange={setIsBusy}
            isDisabled={isSaving}
            columns={1}
            defersServerWrites
            isElevated
          />
        </Box>
        <Button
          width="100%"
          colorScheme="seaGreen"
          marginTop={4}
          isDisabled={isBusy}
          isLoading={isSaving}
          onClick={handleConfirm}
        >
          <FormattedMessage
            {...messages["newTaskShow.addPhotosModal.confirmButton"]}
          />
        </Button>
      </Modal>
    );
  }
);
